import { safeGetItem, safeRemoveItem, safeSetItem } from '../../utils/safeStorage';
import { RegistrationWizardStep, WizardStepInfo } from './types';

function envelopeStorageKey(reservationName: string) {
  return `ra_envelope_${reservationName}`;
}

export function getPersistedEnvelopeId(reservationName: string): string | null {
  return safeGetItem('session', envelopeStorageKey(reservationName));
}

export function persistEnvelopeId(reservationName: string, id: string | null) {
  const key = envelopeStorageKey(reservationName);
  if (id) {
    safeSetItem('session', key, id);
  } else {
    safeRemoveItem('session', key);
  }
}

function rentalAgreementResignKey(reservationName: string) {
  return `ra_resign_${reservationName}`;
}

/**
 * True while a signed rental agreement has been made stale by a later edit. The wizard treats the
 * step as incomplete and the next prepare voids the old envelope instead of reusing it.
 */
export function isRentalAgreementResignPending(reservationName: string): boolean {
  return safeGetItem('session', rentalAgreementResignKey(reservationName)) === '1';
}

export function setRentalAgreementResignPending(reservationName: string, pending: boolean) {
  const key = rentalAgreementResignKey(reservationName);
  if (pending) {
    safeSetItem('session', key, '1');
  } else {
    safeRemoveItem('session', key);
  }
}

function docusignReturnEventKey(reservationName: string) {
  return `ra_docusign_event_${reservationName}`;
}

function getDocuSignReturnEvent(reservationName: string): string | null {
  return safeGetItem('session', docusignReturnEventKey(reservationName));
}

export function setDocuSignReturnEvent(reservationName: string, event: string) {
  safeSetItem('session', docusignReturnEventKey(reservationName), event);
}

export function clearDocuSignReturnEvent(reservationName: string) {
  safeRemoveItem('session', docusignReturnEventKey(reservationName));
}

export function consumeDocuSignReturnEvent(reservationName: string): string | null {
  const event = getDocuSignReturnEvent(reservationName);
  if (event) {
    clearDocuSignReturnEvent(reservationName);
  }
  return event;
}

/** Capture DocuSign ?event= from the current URL into sessionStorage (if present). */
export function captureDocuSignReturnEventFromUrl(reservationName: string): string | null {
  let urlEvent: string | null = null;
  try {
    urlEvent = new URLSearchParams(window.location.search).get('event');
  } catch {
    urlEvent = null;
  }
  if (urlEvent) {
    setDocuSignReturnEvent(reservationName, urlEvent);
  }
  return getDocuSignReturnEvent(reservationName);
}

/** Map DocuSign return event to the wizard step the guest should resume on. */
export function getWizardStepIndexForDocuSignReturn(
  reservationName: string,
  registrationSteps: WizardStepInfo[]
): number | null {
  const event = getDocuSignReturnEvent(reservationName);
  if (!event) {
    return null;
  }

  if (event === 'signing_complete') {
    const identityIndex = registrationSteps.findIndex(
      (s) => s.key === RegistrationWizardStep.IdentityVerification
    );
    if (identityIndex >= 0) {
      return identityIndex;
    }
  }

  const raIndex = registrationSteps.findIndex(
    (s) => s.key === RegistrationWizardStep.RentalAgreement
  );
  return raIndex >= 0 ? raIndex : null;
}
